import { LogLevelDesc } from "loglevel";
import { Client as Conversations, ClientOptions, Conversation, User } from "@twilio/conversations";
import { LogLevel } from "~/modules/cbm";
import { getLogger, Logger, LoggerName } from "~/modules/logger";
import { SessionImpl } from "~/modules/session/Session/SessionImpl";
import type { Session } from "~/modules/session/Session/Session";
import { ContextManager, ContextInstance } from "~/modules/contextManager/ContextManager";
import { ClientOptionsStore } from "~/modules/client/ClientOptions/ClientOptionsStore";
import { ConversationsSdk } from "~/modules/conversations/ConversationSdk";
import { getConfigRegion, parseRegionForTwilsock } from "~/utils/regionUtil";

export class ConversationsSdkImpl implements ConversationsSdk {
    readonly #logger: Logger;

    readonly #session: Session;

    readonly #clientOptions: ClientOptionsStore;

    isInitialized: boolean;

    conversationsClient: Conversations;

    constructor(ctx: ContextManager) {
        this.#session = ctx.retrieveInstanceOf(ContextInstance.SessionImpl, SessionImpl);
        this.#clientOptions = ctx.retrieveInstanceOf(ContextInstance.ClientOptionsStore, ClientOptionsStore);
        this.#logger = getLogger(ctx)(LoggerName.Conversations);
        this.#logger.debug("ConversationsSdk constructed");
    }

    async init(): Promise<void> {
        if (this.isInitialized) {
            return Promise.resolve();
        }
        const configRegion = getConfigRegion(this.#clientOptions);

        const parsedRegion = parseRegionForTwilsock(configRegion);

        const conversationsOptions: ClientOptions = {
            logLevel: this.mapToLogLevel(this.#clientOptions?.logger?.level)
        };

        if (parsedRegion !== undefined) {
            conversationsOptions.region = parsedRegion;
        }

        this.conversationsClient = new Conversations(this.#session.token, conversationsOptions);

        return new Promise<void>((resolve, reject) => {
            const onConnectionStateChanged = (state: string) => {
                if (state === "connected") {
                    this.conversationsClient.removeListener("connectionStateChanged", onConnectionStateChanged);
                    this.isInitialized = true;
                    resolve();
                } else if (state === "denied" || state === "tokenExpired" || state === "connectionError") {
                    this.conversationsClient.removeListener("connectionStateChanged", onConnectionStateChanged);
                    reject(new Error(`Connection state changed to ${state}`));
                }
            };

            this.conversationsClient.on("connectionStateChanged", onConnectionStateChanged);
        });
    }

    async updateToken(token: string): Promise<void> {
        await this.conversationsClient.updateToken(token);
    }

    



    async peekConversationBySid(conversationSid: string): Promise<Conversation> {
        
        
        return this.conversationsClient.peekConversationBySid(conversationSid);
    }

    async getUser(identity: string): Promise<User> {
        
        
        return this.conversationsClient.getUser(identity);
    }

    



    async getConversationBySid(conversationSid: string): Promise<Conversation> {
        

        
        return this.conversationsClient.getConversationBySid(conversationSid);
    }

    private mapToLogLevel(logLevel: LogLevelDesc): LogLevel {
        if (typeof logLevel === "number") {
            switch (logLevel) {
                case 0:
                    return LogLevel.Trace;
                case 1:
                    return LogLevel.Debug;
                case 2:
                    return LogLevel.Info;
                case 3:
                    return LogLevel.Warn;
                case 4:
                    return LogLevel.Error;
                case 5:
                    return LogLevel.Silent;
                default:
                    return LogLevel.Warn;
            }
        } else if (typeof logLevel === "string") {
            const lowercaseLogLevel = logLevel.toLowerCase();
            if (lowercaseLogLevel in LogLevel) {
                return LogLevel[lowercaseLogLevel as keyof typeof LogLevel];
            }
        }
        return LogLevel.Warn;
    }
}
